import React from 'react';
import { useTranslation } from 'react-i18next';
import { MYZMANIM_FIELDS } from '../../config/constants';
import { RoundDirection } from '../../types';
import { ROUND_DIRECTIONS, ROUND_TO_OPTIONS, RoundTo, TimeRuleDraft, ZMAN_KEYS } from '../../utils/timeRuleDraft';
import InfoTooltip from '../common/InfoTooltip';

const inputClass =
  'w-full px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink placeholder:text-ash focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink text-sm';

interface TimeRuleFieldsProps {
  value: TimeRuleDraft;
  onChange: (patch: Partial<TimeRuleDraft>) => void;
  /** Prefix for element ids; must be unique on the page (e.g. 'schedule', 'hours-open'). */
  idPrefix: string;
  /** Name of the fixed/zman radio group; unique per picker on the page. */
  radioName: string;
  /** Label above the fixed-time box. */
  fixedTimeLabel?: string;
  /** More zmanim to offer after the standard ones (opening hours only, e.g. "Shabbos ends"). */
  extraZmanim?: { key: string; label: string }[];
}

/**
 * Fixed time, or zman + offset + rounding. Taken unchanged out of
 * ScheduleEntryForm so the opening-hours form uses exactly the same picker.
 */
export default function TimeRuleFields({ value, onChange, idPrefix, radioName, fixedTimeLabel, extraZmanim = [] }: TimeRuleFieldsProps) {
  const { t } = useTranslation();
  const { ruleType, fixedTime, baseZman, offsetMinutes, roundingEnabled, roundTo, roundDirection } = value;

  return (
    <>
      <div>
        <span className="flex items-center gap-1 text-sm font-medium text-ink mb-2">
          {t('admin.timeType')}
          <InfoTooltip text={t('info.zmanBased')} />
        </span>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="radio"
              name={radioName}
              checked={ruleType === 'fixed'}
              onChange={() => onChange({ ruleType: 'fixed' })}
              className="text-ink focus:ring-ink"
            />
            {t('admin.fixedTime')}
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="radio"
              name={radioName}
              checked={ruleType === 'zmanOffset'}
              onChange={() => onChange({ ruleType: 'zmanOffset' })}
              className="text-ink focus:ring-ink"
            />
            {t('admin.zmanBased')}
          </label>
        </div>
      </div>

      {ruleType === 'fixed' ? (
        <div>
          <label htmlFor={`${idPrefix}-time`} className="block text-sm font-medium text-ink mb-1">
            {fixedTimeLabel ?? t('admin.fixedTime')}
          </label>
          <input
            id={`${idPrefix}-time`}
            type="time"
            required
            value={fixedTime}
            onChange={(e) => onChange({ fixedTime: e.target.value })}
            className={inputClass}
          />
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label htmlFor={`${idPrefix}-zman`} className="flex items-center gap-1 text-sm font-medium text-ink mb-1">
              {t('admin.baseZman')}
              <InfoTooltip text={t('info.baseZman')} />
            </label>
            <select
              id={`${idPrefix}-zman`}
              value={baseZman}
              onChange={(e) => onChange({ baseZman: e.target.value })}
              className={inputClass}
            >
              {ZMAN_KEYS.map((key) => (
                <option key={key} value={key}>
                  {MYZMANIM_FIELDS[key]}
                </option>
              ))}
              {extraZmanim.map((z) => (
                <option key={z.key} value={z.key}>
                  {z.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={`${idPrefix}-offset`} className="flex items-center gap-1 text-sm font-medium text-ink mb-1">
              {t('admin.offsetMinutes')}
              <InfoTooltip text={t('info.offsetAndRounding')} />
            </label>
            <input
              id={`${idPrefix}-offset`}
              type="number"
              value={offsetMinutes}
              onChange={(e) => onChange({ offsetMinutes: Number(e.target.value) })}
              className={inputClass}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={roundingEnabled}
              onChange={(e) => onChange({ roundingEnabled: e.target.checked })}
              className="rounded border-ink text-ink focus:ring-ink"
            />
            {t('admin.enableRounding')}
          </label>

          {roundingEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ps-1">
              <div>
                <label htmlFor={`${idPrefix}-round-to`} className="block text-sm font-medium text-ink mb-1">
                  {t('admin.roundTo')}
                </label>
                <select
                  id={`${idPrefix}-round-to`}
                  value={roundTo}
                  onChange={(e) => onChange({ roundTo: Number(e.target.value) as RoundTo })}
                  className={inputClass}
                >
                  {ROUND_TO_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`${idPrefix}-round-dir`} className="block text-sm font-medium text-ink mb-1">
                  {t('admin.roundDirection')}
                </label>
                <select
                  id={`${idPrefix}-round-dir`}
                  value={roundDirection}
                  onChange={(e) => onChange({ roundDirection: e.target.value as RoundDirection })}
                  className={inputClass}
                >
                  {ROUND_DIRECTIONS.map((d) => (
                    <option key={d} value={d}>
                      {t(`admin.round${d.charAt(0).toUpperCase()}${d.slice(1)}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
